import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { synthesizeSpeech } from "../lib/api";

const AudioCtx = createContext(null);
export const useAudio = () => useContext(AudioCtx);

export const SPEEDS = [0.75, 1, 1.25, 1.5];
const CHUNK_LIMIT = 3500;

// Split long text into <=CHUNK_LIMIT pieces at sentence boundaries.
const chunkText = (text) => {
  const clean = (text || "").trim();
  if (clean.length <= CHUNK_LIMIT) return [clean];
  const sentences = clean.match(/[^.!?।]+[.!?।]*\s*/g) || [clean];
  const chunks = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + s).length > CHUNK_LIMIT && buf) { chunks.push(buf.trim()); buf = ""; }
    if (s.length > CHUNK_LIMIT) {
      for (let i = 0; i < s.length; i += CHUNK_LIMIT) chunks.push(s.slice(i, i + CHUNK_LIMIT));
    } else { buf += s; }
  }
  if (buf.trim()) chunks.push(buf.trim());
  return chunks;
};

// Single global Text-to-Speech player. Only one audio plays at a time, and it can be
// controlled (paused / stopped) from anywhere via the floating GlobalAudioBar.
export const AudioProvider = ({ children }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const [progress, setProgress] = useState(0);
  const [part, setPart] = useState({ i: 0, n: 1 });
  const [speed, setSpeedState] = useState(1);
  const [source, setSource] = useState(null); // { id, label }

  const audioRef = useRef(null);
  const speedRef = useRef(1);
  const langRef = useRef("English");
  const chunksRef = useRef([]);
  const sessionRef = useRef(0); // invalidates stale async callbacks

  const hardStop = useCallback(() => {
    sessionRef.current += 1;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  useEffect(() => () => hardStop(), [hardStop]);

  const playChunk = useCallback(async (session, i) => {
    if (session !== sessionRef.current) return;
    setState("loading");
    setPart({ i, n: chunksRef.current.length });
    try {
      const b64 = await synthesizeSpeech(chunksRef.current[i], langRef.current, speedRef.current);
      if (session !== sessionRef.current) return;
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audio.playbackRate = speedRef.current;
      audio.ontimeupdate = () => {
        if (session === sessionRef.current) setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
      };
      audio.onended = () => {
        if (session !== sessionRef.current) return;
        if (i + 1 < chunksRef.current.length) { playChunk(session, i + 1); }
        else { setState("idle"); setProgress(0); setSource(null); }
      };
      audioRef.current = audio;
      await audio.play();
      if (session === sessionRef.current) setState("playing");
    } catch (e) {
      console.error("tts failed", e);
      if (session === sessionRef.current) { setState("idle"); setSource(null); }
    }
  }, []);

  const playText = useCallback((text, langName, meta = {}) => {
    hardStop();
    const session = sessionRef.current;
    langRef.current = langName || "English";
    chunksRef.current = chunkText(text);
    setSource({ id: meta.id || "audio", label: meta.label || "Listening" });
    setProgress(0);
    playChunk(session, 0);
  }, [hardStop, playChunk]);

  const pause = useCallback(() => { if (audioRef.current) { audioRef.current.pause(); setState("paused"); } }, []);
  const resume = useCallback(() => { if (audioRef.current) { audioRef.current.play(); setState("playing"); } }, []);

  const stop = useCallback(() => {
    hardStop();
    setState("idle"); setProgress(0); setPart({ i: 0, n: 1 }); setSource(null);
  }, [hardStop]);

  const setSpeed = useCallback((s) => {
    setSpeedState(s); speedRef.current = s;
    if (audioRef.current) audioRef.current.playbackRate = s;
  }, []);

  const cycleSpeed = useCallback(() => {
    const next = SPEEDS[(SPEEDS.indexOf(speedRef.current) + 1) % SPEEDS.length];
    setSpeed(next);
  }, [setSpeed]);

  return (
    <AudioCtx.Provider value={{ state, progress, part, speed, source, playText, pause, resume, stop, setSpeed, cycleSpeed }}>
      {children}
    </AudioCtx.Provider>
  );
};
